import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiCheckCircle, FiMinusCircle } from 'react-icons/fi';
import { useCheckMemberStatusMutation } from '../store/apis/business';
import CustomModal from './atoms/CustomModal';
import EventMenuOption from './EventMenuOption';
import Input from './atoms/Input';
import Button from './atoms/Button';
import { BusinessDTO } from '../models/business';
import { IconCard1, IconPlay1 } from './icons';

interface BusinessMemberCardsProps {
  business: BusinessDTO;
}

const BusinessMemberCards = ({ business }: BusinessMemberCardsProps) => {
  const navigate = useNavigate();
  const [checkMemberStatus, { data: memberStatus, isLoading: isCheckingStatus, error }] = useCheckMemberStatusMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dni, setDni] = useState('');
  const [modalType, setModalType] = useState<'accepted' | 'rejected' | 'blacklisted' | 'pending' | 'check' | 'not-found'>('check');

  const handleCheckMemberStatus = () => {
    if (dni && business?.id) {
      checkMemberStatus({ businessId: business.id, person_id: dni });
    }
  };

  useEffect(() => {
    if (memberStatus?.data) {
      setModalType(memberStatus.data || 'check');
    } else if (error) {
      setModalType('not-found');
    }
  }, [memberStatus, error]);

  return (
    <div className='glass-surface'>
      <div className="flex flex-row items-center gap-4">
        <span className="text-xl-semibold lg:display-md-semibold text-text-primary">
          Formá parte de nuestra comunidad
        </span>
      </div>
      <div className='flex flex-col md:flex-row gap-4 mt-4'>
        <div className='w-full'>
          <EventMenuOption
            id="request-membership"
            title="Inscribirme como socio"
            info="Formá parte de nuestra comunidad... ¡inscribiendote como socio!"
            buttonLabel="Inscribirme"
            icon={<IconCard1 className='w-10 h-10 ml-2 mt-1' />}
            onClick={() => navigate('/socios')}
          />
        </div>
        <div className='w-full'>
          <EventMenuOption
            id="request-membership"
            title="Consultar estado de socio"
            info="Ingresá tu DNI y consultá el estado de tu solicitud de socio."
            buttonLabel="Consultar"
            icon={<IconPlay1 className='w-10 h-10 ml-2 mt-1' />}
            onClick={() => {
              setModalType('check');
              setIsModalOpen(true);
            }}
          />
        </div>
      </div>
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        hideCancelButton
      >
        <div className='max-w-md'>
          {modalType === 'check' && <div>
            <h1>Consultar estado de socio</h1>
            <p>Ingresá tu DNI y consultá el estado de tu solicitud de socio.</p>
            <div className='mt-4'>
              <Input
                id="dni"
                label="DNI"
                labelAbove
                placeholder="Ingresá tu DNI"
                value={dni}
                onChange={(e) => setDni(e.target.value)}
              />
            </div>
            <div className='flex mt-4 gap-4'>
              <Button onClick={() => setIsModalOpen(false)} variant='secondary'>Cancelar</Button>
              <Button
                onClick={handleCheckMemberStatus}
                disabled={!dni || isCheckingStatus}
              >
                {isCheckingStatus ? 'Consultando...' : 'Consultar'}
              </Button>
            </div>
          </div>
          }
          {modalType === 'accepted' && <div>
            <div className='flex items-center gap-2'>
              <FiCheckCircle className='text-green-500 w-6 h-6' />
              <h1>Socio aprobado</h1>
            </div>
            <p className='mt-2'>¡Formás parte de nuestra comunidad!</p>
            <div className='mt-4'>
              <Button onClick={() => setIsModalOpen(false)} variant='primary'>Aceptar</Button>
            </div>
          </div>
          }
          {(modalType === 'rejected' || modalType === 'blacklisted') && <div>
            <div className='flex items-center gap-2'>
              <FiMinusCircle className='text-red-500 w-6 h-6' />
              <h1>Solicitud rechazada</h1>
            </div>
            <p className='mt-2'>Tu solicitud se encuentra rechazada, si crees que esto es un error comunicate con el productor {business?.name}</p>
            <div className='mt-4'>
              <Button onClick={() => setIsModalOpen(false)} variant='primary'>Aceptar</Button>
            </div>
          </div>
          }
          {modalType === 'pending' && <div>
            <div className='flex items-center gap-2'>
              <FiAlertCircle className='text-yellow-500 w-6 h-6' />
              <h1>Solicitud pendiente</h1>
            </div>
            <p className='mt-2'>Tu solicitud se encuentra pendiente a ser aprobada. Una vez aprobada se te informará por mail.</p>
            <div className='mt-4'>
              <Button onClick={() => setIsModalOpen(false)} variant='primary'>Aceptar</Button>
            </div>
          </div>
          }
          {modalType === 'not-found' && <div>
            <div className='flex items-center gap-2'>
              <FiAlertCircle className='text-yellow-500 w-6 h-6' />
              <h1>Solicitud no encontrada</h1>
            </div>
            <p className='mt-2'>No se encontró ninguna solicitud de socio con ese DNI.</p>
            <div className='mt-4'>
              <Button onClick={() => setIsModalOpen(false)} variant='primary'>Aceptar</Button>
            </div>
          </div>
          }
        </div>
      </CustomModal>
    </div>
  );
};

export default BusinessMemberCards;
